/**
 * Creatives Insight Tab
 *
 * Left: Health score + AI summary + next steps
 * Right: Top and bottom performer ad cards
 */

import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { analysisApi } from './api'
import type { CreativeInsightResponse, InsightAdPerformer } from './types'

function formatCurrency(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`
  return `$${v.toFixed(2)}`
}

function HealthDot({ label }: { label: string }) {
  const color =
    label === 'Healthy' ? 'bg-tf-success' :
    label === 'Moderate' ? 'bg-tf-warning' :
    label === 'Fatigued' ? 'bg-orange-500' : 'bg-tf-feedback-error-bg'
  return <span className={`w-3 h-3 rounded-full inline-block ${color}`} />
}

function PerformerCard({ ad, rank, variant }: { ad: InsightAdPerformer; rank: number; variant: 'top' | 'bottom' }) {
  const borderColor = variant === 'top' ? 'border-green-200 bg-tf-feedback-success-bg/30' : 'border-red-200 bg-tf-feedback-error-bg/30'
  return (
    <div className={`rounded-lg border p-3 ${borderColor}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-tf-text-muted">#{rank}</span>
        <div className="flex items-center gap-1.5">
          <HealthDot label={ad.health_label} />
          <span className="text-xs font-medium text-tf-text-muted">{ad.health_label}</span>
        </div>
      </div>
      <p className="text-sm font-medium text-tf-text truncate mb-2" title={ad.campaign_name}>
        {ad.campaign_name}
      </p>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <span className="text-tf-text-muted block">ROAS</span>
          <span className="font-semibold text-tf-text">{ad.roas.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-tf-text-muted block">Spend</span>
          <span className="font-semibold text-tf-text">{formatCurrency(ad.spend)}</span>
        </div>
        <div>
          <span className="text-tf-text-muted block">CTR</span>
          <span className="font-semibold text-tf-text">{ad.ctr.toFixed(2)}%</span>
        </div>
      </div>
    </div>
  )
}

interface CreativesInsightTabProps {
  startDate: string
  endDate: string
}

export function CreativesInsightTab({ startDate, endDate }: CreativesInsightTabProps) {
  const [data, setData] = useState<CreativeInsightResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchReport = useCallback(async (regenerate = false) => {
    if (regenerate) setRegenerating(true)
    else setLoading(true)
    setError(null)
    try {
      const report = await analysisApi.getInsightReport(startDate, endDate, regenerate)
      setData(report)
    } catch (err) {
      console.error('Error fetching insight report:', err)
      setError('Failed to load creatives insight report')
    } finally {
      setLoading(false)
      setRegenerating(false)
    }
  }, [startDate, endDate])

  useEffect(() => { fetchReport(false) }, [fetchReport])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Icons.Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-sm text-tf-text-muted">Analyzing creative health...</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Icons.AlertCircle className="w-8 h-8 text-red-400" />
        <p className="text-sm text-red-600">{error || 'No data available'}</p>
        <button onClick={() => fetchReport(false)} className="mt-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Cache + Regenerate */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-tf-text-muted">
          {data.from_cache ? (
            <><Icons.Database className="w-3.5 h-3.5" /><span>Cached from {new Date(data.generated_at).toLocaleString()}</span></>
          ) : (
            <><Icons.Sparkles className="w-3.5 h-3.5 text-primary" /><span>Generated {new Date(data.generated_at).toLocaleString()}</span></>
          )}
        </div>
        <button onClick={() => fetchReport(true)} disabled={regenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/30 rounded-lg hover:bg-primary/15 disabled:opacity-50">
          {regenerating ? <><Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />Re-analyzing...</> : <><Icons.RefreshCw className="w-3.5 h-3.5" />Regenerate</>}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: AI Analysis */}
        <div className="w-full lg:w-2/5 flex flex-col gap-4">
          {/* Health Score */}
          <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
            <div className="flex items-center gap-3">
              <HealthDot label={data.overall_health_label} />
              <span className="text-lg font-bold text-tf-text">
                Creative Health: {data.overall_health_score.toFixed(1)}/10 — {data.overall_health_label}
              </span>
            </div>
            <p className="text-xs text-tf-text-muted mt-1">{data.total_ads} campaigns analyzed</p>
          </div>

          {/* Summary */}
          {data.report.body && (
            <div>
              <h3 className="text-base font-semibold text-tf-text mb-2">Summary</h3>
              <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 prose prose-sm prose-gray max-w-none">
                <ReactMarkdown>{data.report.body}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Next Steps */}
          {data.report.action_items && (
            <div>
              <h3 className="text-base font-semibold text-tf-text mb-2">Next Steps</h3>
              <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 prose prose-sm prose-gray max-w-none">
                <ReactMarkdown>{data.report.action_items}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Right: Top / Bottom Performers */}
        <div className="w-full lg:w-3/5 flex flex-col gap-4">
          {/* Top Performers */}
          <div>
            <h3 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1.5">
              <Icons.TrendingUp className="w-4 h-4" />
              Top Performers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.top_performers.map((ad, i) => (
                <PerformerCard key={ad.campaign_id} ad={ad} rank={i + 1} variant="top" />
              ))}
            </div>
          </div>

          {/* Bottom Performers */}
          <div>
            <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5">
              <Icons.TrendingDown className="w-4 h-4" />
              Bottom Performers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.bottom_performers.map((ad, i) => (
                <PerformerCard key={ad.campaign_id} ad={ad} rank={i + 1} variant="bottom" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
